import React, { useState, useEffect, useMemo } from "react";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";
import { HighlightText, KpiIcon } from "../../Quantative/component/KpiIcons";
import InfoTip from "../../../../globalComponents/ui/InfoTip";
import TabularDynamicForm from "./TabularDynamicForm";
import QualitativeHistoryModal from "./QualitativeHistoryModal";
import QualitativeDocumentUploadModal from "./QualitativeDocumentUploadModal";
import { normalizeAnswers, conformGridToQuestion, countGridValues } from "../services/answerShape";
import ViewNoteButton, { noteToText } from "./ViewNoteButton";
import { useQualitativeCapabilities } from "../hooks/useQualitativeCapabilities";
import { LOCKED, OPEN } from "../../KpiLock/helpers";

/** A KPI with no deadline configured, so nothing to lock. */
const NO_LOCK = { status: OPEN, dueDate: null, lockedLocationIds: [] };

/**
 * Marks a qualitative KPI whose due date has passed.
 *
 * Qualitative answers carry no location, so the lock is whole-KPI — there is no
 * partial state to show as there is on the quantitative screen.
 * @param {object} props
 * @param {object} props.lock the KPI's lock state.
 * @returns {JSX.Element|null} the badge, or nothing when the KPI is open.
 */
const QualLockBadge = ({ lock }) => {
  if (!lock || lock.status === OPEN) return null;
  return (
    <span
      title={`Closed for entry on ${lock.dueDate}. Request access from an administrator to reopen it.`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, marginLeft: 8,
        padding: "1px 7px", borderRadius: 10, verticalAlign: "middle",
        fontSize: 10.5, fontWeight: 600, fontFamily: "var(--font-mono)",
        color: "#9f1239", background: "#ffe4e6", border: "1px solid #fda4af",
      }}
    >
      <KpiIcon name="lock" size={9} color="#9f1239" />
      Locked
    </span>
  );
};

/* ── AVATAR + CELL COMPONENTS (shared with Quantative) ── */
const AVATAR_COLORS = [
  "#2563eb",
  "#16a34a",
  "#dc2626",
  "#d97706",
  "#7c3aed",
  "#0891b2",
  "#be185d",
  "#0f766e",
  "#ea580c",
  "#4f46e5",
];
const avatarBg = (id) => AVATAR_COLORS[Number(id) % AVATAR_COLORS.length];

const UserAvatar = ({ user, size = 26, overlap = false }) => {
  const [hovered, setHovered] = useState(false);
  const initials =
    [user.first_name?.[0], user.last_name?.[0]]
      .filter(Boolean)
      .join("")
      .toUpperCase() || "?";
  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
        marginLeft: overlap ? -7 : 0,
        zIndex: hovered ? 10 : 1,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: avatarBg(user.id),
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: size * 0.36,
          fontWeight: 700,
          fontFamily: "var(--font-sans)",
          border: "2px solid #fff",
          flexShrink: 0,
          cursor: "default",
          userSelect: "none",
        }}
      >
        {initials}
      </div>
      {hovered && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1e293b",
            color: "#fff",
            borderRadius: 6,
            padding: "5px 10px",
            fontSize: 11,
            fontWeight: 600,
            fontFamily: "var(--font-sans)",
            whiteSpace: "nowrap",
            zIndex: 9999,
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            pointerEvents: "none",
          }}
        >
          <div>{user.name || `${user.first_name} ${user.last_name}`}</div>
          {user.email && (
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
              {user.email}
            </div>
          )}
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              border: "5px solid transparent",
              borderTopColor: "#1e293b",
            }}
          />
        </div>
      )}
    </div>
  );
};

const UserDropdown = ({ users = [], open, onClose, anchorWidth = 260 }) => {
  const [query, setQuery] = useState("");
  const filtered = users.filter((u) =>
    (u.name || `${u.first_name} ${u.last_name}`)
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  if (!open) return null;
  return (
    <div
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        width: anchorWidth,
        background: "var(--surface)",
        border: "1.5px solid var(--rule)",
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        zIndex: 9999,
        padding: 8,
      }}
      onMouseLeave={onClose}
    >
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search user…"
        style={{
          width: "100%",
          padding: "6px 8px",
          fontSize: 11,
          borderRadius: 6,
          border: "1px solid var(--rule)",
          marginBottom: 6,
          fontFamily: "var(--font-sans)",
        }}
      />
      <div style={{ maxHeight: 180, overflowY: "auto" }}>
        {filtered.length ? (
          filtered.map((u) => (
            <div
              key={u.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "6px 8px",
                borderRadius: 6,
              }}
            >
              <UserAvatar user={u} size={26} />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--ink)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {u.name || `${u.first_name} ${u.last_name}`}
                </div>
                {u.email && (
                  <div
                    style={{
                      fontSize: 10,
                      color: "var(--ink-4)",
                      fontFamily: "var(--font-mono)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {u.email}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div
            style={{
              padding: "12px 8px",
              fontSize: 11,
              color: "var(--ink-4)",
              textAlign: "center",
            }}
          >
            No users found
          </div>
        )}
      </div>
    </div>
  );
};

const AssignedToCell = ({ users, width = 150 }) => {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(users) ? users : [];
  const MAX = 3;
  const visible = list.slice(0, MAX);
  const extra = list.length - MAX;
  if (!list.length)
    return (
      <div style={{ width, padding: "14px 10px" }}>
        <span
          style={{ fontSize: 11, color: "var(--ink-4)", fontStyle: "italic" }}
        >
          Unassigned
        </span>
      </div>
    );
  return (
    <div
      style={{ width, padding: "12px 10px", position: "relative" }}
      onMouseLeave={() => setOpen(false)}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        {visible.map((u, i) => (
          <UserAvatar key={u.id} user={u} overlap={i > 0} />
        ))}
        {extra > 0 && (
          <div
            onMouseEnter={() => setOpen(true)}
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              background: "#f1f5f9",
              border: "2px solid #fff",
              marginLeft: -7,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9.5,
              fontWeight: 700,
              color: "var(--ink-3)",
              cursor: "default",
            }}
          >
            +{extra}
          </div>
        )}
      </div>
      <UserDropdown users={list} open={open} onClose={() => setOpen(false)} />
    </div>
  );
};

const AssignedByCell = ({ user, width = 130 }) => {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(user) ? user : [];
  const MAX = 3;
  const visible = list.slice(0, MAX);
  const extra = list.length - MAX;
  if (!list.length)
    return (
      <div style={{ width, padding: "14px 10px" }}>
        <span
          style={{ fontSize: 11, color: "var(--ink-4)", fontStyle: "italic" }}
        >
          Unassigned
        </span>
      </div>
    );
  return (
    <div
      style={{ width, padding: "12px 10px", position: "relative" }}
      onMouseLeave={() => setOpen(false)}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        {visible.map((u, i) => (
          <UserAvatar key={u.id} user={u} overlap={i > 0} />
        ))}
        {extra > 0 && (
          <div
            onMouseEnter={() => setOpen(true)}
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              background: "#f1f5f9",
              border: "2px solid #fff",
              marginLeft: -7,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9.5,
              fontWeight: 700,
              color: "var(--ink-3)",
              cursor: "default",
            }}
          >
            +{extra}
          </div>
        )}
      </div>
      <UserDropdown users={list} open={open} onClose={() => setOpen(false)} />
    </div>
  );
};

/**
 * Answered / unanswered pill. `answered` is the single source of truth from the
 * parent list (the FY-wide answered-id set), so the badge can't drift from the
 * row's own on-expand answer fetch.
 */
const StatusCell = ({ answered, width = 120 }) => {
  const style = answered
    ? { bg: "#dcfce7", color: "#16a34a", label: "Answered" }
    : { bg: "#fef3c7", color: "#d97706", label: "Unanswered" };
  return (
    <div style={{ width, flexShrink: 0, padding: "14px 10px", display: "flex", alignItems: "center" }}>
      <span
        style={{
          padding: "4px 8px",
          borderRadius: 20,
          fontSize: 10,
          fontWeight: 700,
          background: style.bg,
          color: style.color,
          fontFamily: "var(--font-sans)",
          whiteSpace: "nowrap",
        }}
      >
        {style.label}
      </span>
    </div>
  );
};

const QualitativeRow = ({
  question,
  answered = false,
  index,
  financialYearId,
  searchQuery = "",
  selected,
  onToggle,
  expanded,
  onExpand,
  onAddData,
  onPrefill,
  showAssignments = true,
  isEven,
  lock = NO_LOCK,
  accessRequested = false,
  accessDeclined = false,
  requestingAccess = false,
  onRequestAccess,
  canDecideAccess = false,
  pendingRequestIds = [],
  onApproveAccess,
  onRejectAccess,
}) => {
  const { canCreate, canView } = useQualitativeCapabilities();
  const [extensionDays, setExtensionDays] = useState(4);
  const isLocked = lock.status === LOCKED;
  const hasPendingToDecide = canDecideAccess && pendingRequestIds.length > 0;
  const [answerData, setAnswerData] = useState(
    question.answer ||
      (question.reporting_question_answers &&
        question.reporting_question_answers[0]) ||
      null,
  );
  const [hasFetched, setHasFetched] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [viewDocsOpen, setViewDocsOpen] = useState(false);
  // qualitativeQuestionAnswer already returns attached documents inline —
  // no separate /documents fetch needed.
  const documents = answerData?.documents ?? [];

  const fetchAnswer = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}qualitativeQuestionAnswer`,
      {},
      { financialYearId, questionId: question.questionId },
      "GET",
    );
    const answers = normalizeAnswers(data?.answers);
    if (isSuccess && answers.length > 0) {
      setAnswerData(answers[0]);
    }
    setHasFetched(true);
  };

  const handleViewDocsOpen = async () => {
    if (!hasFetched) await fetchAnswer();
    setViewDocsOpen(true);
  };

  // ONLY fetch when expanded to prevent 800+ requests on mount
  useEffect(() => {
    if (expanded && !hasFetched) {
      fetchAnswer();
    }
  }, [expanded, hasFetched, question, financialYearId]);

  const gridData = useMemo(() => {
    if (!answerData?.answer) return null;
    try {
      const raw = answerData.answer;
      const parsed = Array.isArray(raw) ? raw : JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      return conformGridToQuestion(parsed, question?.details, answerData?.columnGroups);
    } catch (e) {
      return null;
    }
  }, [answerData, question]);

  const hasAnswerContent = countGridValues(gridData) > 0 || !!noteToText(answerData?.note)?.trim();

  const handleRowClick = (e) => {
    if (e.target.closest(".kpi-no-expand")) return;
    onExpand?.();
  };

  const baseBg = isEven ? "#ffffff" : "#f8fafd";
  const title =
    question.title || question.question_explanation || "Qualitative Question";

  return (
    <div
      className={`kpi-row-container ${expanded ? "expanded" : ""} ${isEven ? "even" : "odd"}`}
      data-question-id={question.id}
      style={{
        borderBottom: "1.5px solid #dde2ea",
        background: expanded ? "#eef4ff" : selected ? "#e6eeff" : baseBg,
        transition: "background 0.15s ease",
        boxShadow: expanded ? "inset 3px 0 0 var(--accent)" : "none",
      }}
    >
      {/* ── MAIN ROW ── */}
      <div
        onClick={handleRowClick}
        style={{
          display: "flex",
          alignItems: "stretch",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        {/* Checkbox */}
        <div
          className="kpi-no-expand"
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 46,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px 0",
          }}
        >
          <input
            type="checkbox"
            className="kpi-checkbox"
            checked={selected}
            onChange={onToggle}
          />
        </div>

        {/* Row number */}
        <div
          style={{
            width: 32,
            flexShrink: 0,
            display: "flex",
            alignItems: "flex-start",
            paddingTop: 18,
            fontSize: 12,
            fontWeight: 700,
            color: "var(--ink-4)",
            fontFamily: "var(--font-mono)",
          }}
        >
          {index + 1}.
        </div>

        {/* Question name */}
        <div style={{ flex: 1, minWidth: 0, padding: "14px 14px 14px 10px" }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: expanded ? 700 : 500,
              color: expanded ? "#0f172a" : "var(--ink)",
              lineHeight: 1.5,
              fontFamily: "var(--font-sans)",
              letterSpacing: "-0.01em",
              wordBreak: "break-word",
            }}
          >
            <HighlightText text={title} query={searchQuery} />
            <InfoTip text={question.description} />
            <QualLockBadge lock={lock} />
          </div>
        </div>

        {/* Answered / unanswered status */}
        <StatusCell answered={answered} width={120} />

        {showAssignments && (
          <>
            {/* Assigned To */}
            <AssignedToCell users={question.assigned_to} width={150} />

            {/* Assigned By */}
            <AssignedByCell user={question.assigned_by} width={130} />
          </>
        )}

        {/* Chevron */}
        <div
          style={{
            width: 44,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              border: "1.5px solid var(--rule)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              background: expanded ? "var(--accent-3)" : "transparent",
              borderColor: expanded ? "var(--accent-2)" : "var(--rule)",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <KpiIcon
              name="chevron"
              size={13}
              color={expanded ? "var(--accent)" : "var(--ink-4)"}
            />
          </div>
        </div>
      </div>

      {/* ── ACCORDION PANEL ── */}
      {expanded && (
        <div className="kpi-slide-down">
          <div
            style={{
              padding: "14px 20px 18px",
              background: "linear-gradient(to bottom, #f8faff, #f4f6fe)",
              borderTop: "2px solid var(--accent-2)",
            }}
          >
            {gridData && gridData.length > 0 && (
              <div
                style={{
                  background: "var(--surface)",
                  borderRadius: 12,
                  border: "1px solid var(--rule)",
                  overflow: "hidden",
                  marginBottom: 16,
                }}
              >
                <TabularDynamicForm
                  question={question}
                  gridData={gridData}
                  setGridData={() => {}}
                  readOnly={true}
                  columnGroups={answerData?.columnGroups || null}
                  rowNames={answerData?.rowNames || []}
                />
              </div>
            )}

            {!question.isReadOnly && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <ViewNoteButton note={answerData?.note} />
              {canCreate && (
                <button
                  className="kpi-action-btn kpi-btn-add"
                  disabled={isLocked}
                  title={isLocked ? `Closed for entry on ${lock.dueDate}` : undefined}
                  style={isLocked ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isLocked) return;
                    onAddData(question, answerData, fetchAnswer);
                  }}
                >
                  <KpiIcon name={hasAnswerContent ? "edit" : "add"} size={11} />
                  {hasAnswerContent ? " Edit Data" : " Add Data"}
                </button>
              )}
              {canView && (
                <button
                  className="kpi-action-btn kpi-btn-history"
                  onClick={(e) => {
                    e.stopPropagation();
                    setHistoryOpen(true);
                  }}
                >
                  <KpiIcon name="history" size={11} /> History
                </button>
              )}
              {lock.status !== OPEN && !hasPendingToDecide && (
                <button
                  className="kpi-action-btn kpi-btn-history"
                  disabled={accessRequested || requestingAccess}
                  title={
                    accessRequested
                      ? "An administrator has yet to review this request"
                      : accessDeclined
                        ? "An administrator declined the last request. You can ask again."
                        : undefined
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequestAccess?.(question, lock);
                  }}
                >
                  <KpiIcon name="unlock" size={11} />
                  {accessRequested
                    ? " Access requested"
                    : requestingAccess
                      ? " Requesting…"
                      : accessDeclined
                        ? " Request declined — ask again"
                        : " Request for Access"}
                </button>
              )}
              {hasPendingToDecide && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 10px", height: 30, borderRadius: 6, background: "#fffbeb", border: "1px solid #fcd34d" }}
                >
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: "#92400e", fontFamily: "var(--font-mono)" }}>
                    {pendingRequestIds.length} access request{pendingRequestIds.length > 1 ? "s" : ""}
                  </span>
                  <label style={{ fontSize: 11.5, color: "#92400e" }}>
                    Extend by
                    <select
                      value={extensionDays}
                      onChange={(e) => setExtensionDays(Number(e.target.value))}
                      style={{ marginLeft: 6, fontSize: 11.5, padding: "2px 4px", borderRadius: 4, border: "1px solid #fcd34d" }}
                    >
                      {[2, 3, 4, 7, 15, 30].map((d) => (
                        <option key={d} value={d}>{d} days</option>
                      ))}
                    </select>
                  </label>
                  <button className="kpi-action-btn kpi-btn-add" onClick={() => onApproveAccess?.(pendingRequestIds, extensionDays)}>
                    Approve
                  </button>
                  <button className="kpi-action-btn kpi-btn-history" onClick={() => onRejectAccess?.(pendingRequestIds)}>
                    Reject
                  </button>
                </div>
              )}
              {canView && (
                <button
                  className="kpi-action-btn kpi-btn-history"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDocsOpen();
                  }}
                >
                  <KpiIcon name="audit" size={11} /> View Documents
                </button>
              )}
              {canCreate && onPrefill && (
                <button
                  className="kpi-action-btn kpi-btn-history"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPrefill(question.questionId || question.id, fetchAnswer);
                  }}
                >
                  <KpiIcon name="copy" size={11} /> Prefill From Previous Year
                </button>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      <QualitativeHistoryModal
        show={historyOpen}
        onHide={() => setHistoryOpen(false)}
        question={question}
        financialYearId={financialYearId}
      />
      {/* Read-only — evidence is attached via Add/Edit Data; this button is
          purely for viewing what's already there, never for uploading. */}
      <QualitativeDocumentUploadModal
        show={viewDocsOpen}
        onHide={() => setViewDocsOpen(false)}
        questionId={question.questionId}
        questionTitle={question.title || question.question_explanation}
        financialYearId={financialYearId}
        documents={documents}
        canUpload={false}
      />
    </div>
  );
};

export default QualitativeRow;
